import { bool, object } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormInput, RadioGroup, Skeleton, useCartContext } from 'components';
import { processString } from 'helpers';
import { useCivility, useFetch, useUser } from 'hooks';

/**
 * Contact & Shipping address form used in the travel and leisure purchase process
 * Must be used inside a form context
 */
const UserContactAddressForm = ({ user, userLoading }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { cart } = useCartContext();
  const { call: addressesCall, loading: addressesLoading } = useFetch({ withSetState: false });
  const { civilityOptions } = useCivility('shipping.civility');

  /**
   * In purchase tunnel civility should be sent as integer
   * 1 for Mr and 2 for Mme
   * Only for checkout API
   */
  const contactCivilityOptions = useMemo(() => {
    return civilityOptions.map((option) => ({
      ...option,
      value: option.value === 'Mr' ? '1' : '2'
    }));
  }, []);

  const pageType = useMemo(() => {
    return location.pathname.includes('voyage') ? 'travel' : 'other';
  }, [location.pathname]);

  const { setValue } = useFormContext();

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getAddresses();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getAddresses = async () => {
    if (!currentUser?.serialNumber) return;
    const { data } =
      (await addressesCall({
        service: 'castelis',
        path: `/users/addresses/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    const userShippingAddress = data?.body?.addresses?.find((address) => address?.type === 'Livraison') || null;
    const rawShippingAddress = cart?.shippingAddress || userShippingAddress || null;

    const shippingAddress = {
      civility: rawShippingAddress?.civility === 'Mr' || rawShippingAddress?.civility === 1 ? '1' : '2',
      firstName: rawShippingAddress?.firstName || rawShippingAddress?.firstname,
      name: rawShippingAddress?.name || rawShippingAddress?.lastname,
      address1: rawShippingAddress?.address1 || rawShippingAddress?.street,
      address2: rawShippingAddress?.address2 || rawShippingAddress?.streetExtra,
      address3: rawShippingAddress?.address3 || rawShippingAddress?.streetExtra2,
      postalCode: rawShippingAddress?.postalCode,
      city: rawShippingAddress?.city
    };

    if (!shippingAddress) return;

    // Prefill shipping address fields with user default shipping address
    // or with address previously saved in the cart
    const formStateOptions = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
    setValue('shipping.civility', shippingAddress.civility, formStateOptions);
    setValue('shipping.firstname', shippingAddress.firstName, formStateOptions);
    setValue('shipping.lastname', shippingAddress.name, formStateOptions);
    setValue('shipping.street', shippingAddress.address1, formStateOptions);
    setValue('shipping.complement1', shippingAddress.address2, formStateOptions);
    setValue('shipping.complement2', shippingAddress.address3, formStateOptions);
    setValue('shipping.postalCode', shippingAddress.postalCode, formStateOptions);
    setValue('shipping.city', shippingAddress.city, formStateOptions);
  };

  return (
    <>
      {/* USER CONTACT : EMAIL & PHONE */}
      <h2 className="mb-6 underlined-blue after:hidden h3 lg:after:block lg:h2">
        {t('purchase:shipping.contactTitle')}
      </h2>

      {/* CONTACT SKELETON */}
      {userLoading && (
        <div className="grid grid-cols-12 gap-2 items-center">
          <Skeleton classNames="col-span-4 h-12" />
          <Skeleton classNames="col-span-8 h-12 mb-2" />
          <Skeleton classNames="col-span-4 h-12" />
          <Skeleton classNames="col-span-8 h-12" />
        </div>
      )}

      {!userLoading && (
        <>
          <div className="grid grid-cols-12 gap-2 items-center mb-4">
            <div className="col-span-4 text-sm uppercase">{t('form:email')}</div>
            <p className="col-span-8">{user?.email}</p>
          </div>

          <div className="grid grid-cols-12 gap-2 items-center mb-8">
            <div className="col-span-4 text-sm uppercase">{t('form:phone')}</div>
            <p className="col-span-8">{user?.phone}</p>
          </div>
        </>
      )}

      {/* SHIPPING ADDRESS */}
      <h2 className="mb-6 underlined-blue after:hidden h3 lg:after:block h3 lg:h2">
        {t(`purchase:shipping.${pageType}ShippingTitle`)}
      </h2>

      {/* ADDRESS SKELETON */}
      {addressesLoading && (
        <>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`shipping-field-skeleton-${index}`} className="grid grid-cols-12 gap-2 items-center">
              <Skeleton classNames="col-span-4 h-12" />
              <Skeleton classNames="col-span-8 h-12" />
            </div>
          ))}
        </>
      )}

      {!addressesLoading && (
        <form className="shipping-address-form">
          {/* CIVILITY */}
          <div className="grid grid-cols-12 items-center gap-2 mb-4">
            <p className="col-span-4 text-gray-600 text-sm uppercase md:col-span-4">{t('form:civility.label')}</p>

            <RadioGroup
              radioOptions={contactCivilityOptions}
              row
              rules={{ required: true }}
              className="col-span-8 -ml-4"
            />
          </div>

          {/* LASTNAME & FIRSTNAME */}
          <div className="grid grid-cols-12 items-center gap-4 mb-2">
            {/* Tablet & Desktop Label */}
            <p className="hidden col-span-4 text-gray-600 uppercase text-sm md:block">
              <span className="mr-1">{t('form:lastname')}</span>
              <span>{t('form:firstname')}</span>
            </p>

            {/* Mobile Label */}
            <p className="col-span-4 text-gray-600 uppercase text-sm md:hidden">{t('form:lastname')}</p>

            <FormInput
              name="shipping.lastname"
              placeholder={t('form:lastname')}
              rules={{ required: true }}
              className="col-span-8 md:col-span-4"
              onBlur={(value) => setValue('lastname', processString(value))}
            />

            {/* Mobile Label */}
            <p className="col-span-4 text-gray-600 uppercase text-sm md:hidden">{t('form:firstname')}</p>

            <FormInput
              name="shipping.firstname"
              placeholder={t('form:firstname')}
              rules={{ required: true }}
              className="col-span-8 md:col-span-4"
              onBlur={(value) => setValue('firstname', processString(value))}
            />
          </div>

          {/* STREET */}
          <div className="grid grid-cols-12 items-center gap-4 mb-2">
            <p className="col-span-4 text-gray-600 uppercase text-sm">{t('form:address')}</p>
            <FormInput
              name="shipping.street"
              placeholder={t('form:address')}
              rules={{ required: true }}
              className="col-span-8"
            />
          </div>

          {/* STREET COMPLEMENT 1 */}
          <div className="grid grid-cols-12 items-center gap-4 mb-2">
            <p className="col-span-4 text-gray-600 uppercase text-sm">{t('form:addressComplementary')}</p>
            <FormInput
              name="shipping.complement1"
              placeholder={t('form:addressComplementary')}
              className="col-span-8"
            />
          </div>

          {/* STREET COMPLEMENT 2 */}
          <div className="grid grid-cols-12 items-center gap-4 mb-2">
            <p className="col-span-4 text-gray-600 uppercase text-sm">{t('form:addressComplementary1')}</p>
            <FormInput
              name="shipping.complement2"
              placeholder={t('form:addressComplementary1')}
              className="col-span-8"
            />
          </div>

          {/* POSTAL CODE & CITY */}
          <div className="grid grid-cols-12 items-center gap-4 mb-2">
            {/* Tablet & Desktop Label */}
            <p className="hidden col-span-4 text-gray-600 uppercase text-sm md:block">{t('form:cityPostalCode')}</p>

            {/* Mobile Label */}
            <p className="col-span-4 text-gray-600 uppercase text-sm md:hidden">{t('form:postalCode')}</p>
            <FormInput
              name="shipping.postalCode"
              placeholder={t('form:postalCode')}
              rules={{ required: true }}
              className="col-span-8 md:col-span-4"
            />

            {/* Mobile Label */}
            <p className="col-span-4 text-gray-600 uppercase text-sm md:hidden">{t('form:city')}</p>
            <FormInput
              name="shipping.city"
              placeholder={t('form:city')}
              rules={{ required: true }}
              className="col-span-8 md:col-span-4"
            />
          </div>
        </form>
      )}
    </>
  );
};

UserContactAddressForm.propTypes = {
  user: object,
  userLoading: bool
};

export default UserContactAddressForm;
